// Pure helpers for the Inbox (047 step 5, D54–D62): the list's views and cells, and the ticket
// page's title, head line, cards and timeline. No React and no MUI: Jest loads this file. Times
// print in the zone given, or in the reader's.
import { permissions, STAFF_ROLES } from "../config/permissions";
import { STATUS_LABELS, TICKET_STATUSES } from "./ticketContract";
import { isStaffMessage, ticketNumberLabel } from "./inboxPresentation";
import { kindLabel, toDate } from "./ticketPresentation";
import { countText, dateTimeLines } from "./kitPresentation";
import { TICKET_CATEGORIES, endOf, partyOf, statusWord } from "./lessonPresentation";
import { personName, ticketLessonLabel, typeChip } from "./studentDetail";
import { sessionsLabel } from "./studentPresentation";
import { reviewsLine } from "./tutorDetail";

// "30 Aug 2026, 19:12", or "" without a date.
const at = (value, timeZone) => {
  const when = dateTimeLines(value, { timeZone });
  return when ? `${when.date}, ${when.time}` : "";
};

const categoryLabel = (value) => TICKET_CATEGORIES.find((c) => c.value === value)?.label || value || "";

// A system ticket stores no subject or start time (lessonLifecycle.js ticketFor), so the page
// takes them from the lesson once it is read.
const lessonOf = (ticket, booking) => ({
  ...ticket,
  subject: ticket?.subject || booking?.subject,
  startTimeUTC: ticket?.startTimeUTC || booking?.startTimeUTC,
});

// ── The list (D55, D56) ──

// The four status views, in the contract's order and words; Submitted comes first.
export const INBOX_VIEWS = TICKET_STATUSES.map((key) => ({ key, label: STATUS_LABELS[key] }));

// The head's line: "12 tickets"; "Loading tickets…" until the counts land; nothing when the
// chosen status's count failed.
export const inboxHeadLine = ({ total, loading = false } = {}) => {
  if (loading) return "Loading tickets…";
  return typeof total === "number" ? `${countText(total)} ${total === 1 ? "ticket" : "tickets"}` : "";
};

// The empty table's line: "No tickets in Waiting for You."
export const inboxEmptyText = (status) => `No tickets in ${STATUS_LABELS[status] || status}.`;

// The REASON cell's first line, as the Tickets tabs print it: "Reported lesson", "Request · Payments".
export const ticketReasonLine = (ticket) =>
  `${kindLabel(ticket?.kind)}${ticket?.category ? ` · ${categoryLabel(ticket.category)}` : ""}`;

// A person's role in a word: "Tutor"; a student is "Parent" or "Adult", as on the student page
// (044 D36); a staff account is "Staff". "" for a document not read yet, or any other role.
export const roleWord = (user) => {
  if (user?.role === "teacher") return "Tutor";
  if (user?.role === "student") return typeChip(user);
  return STAFF_ROLES.includes(user?.role) ? "Staff" : "";
};

// The RAISED BY cell: the person and their role ("Opened by staff" under a staff ticket's person),
// or the system. `users` holds the documents read so far, by id; the name is "—" until one lands.
export const raisedByCell = (ticket, users = {}) => {
  if (!ticket?.raisedBy) return { system: true, id: null, name: "System", sub: "Raised automatically" };
  const user = users[ticket.raisedBy];
  return {
    system: false,
    id: ticket.raisedBy,
    name: personName(user) || "—",
    sub: ticket.source === "staff" ? "Opened by staff" : roleWord(user),
  };
};

// ── The head (D57, D59) ──

// The title: the kind with the lesson's subject, or with a request's category. `booking` is the
// lesson, once read.
export const ticketTitle = (ticket, booking) => {
  const kind = kindLabel(ticket?.kind) || "Ticket";
  const detail = ticket?.kind === "user_request" ? categoryLabel(ticket?.category) : lessonOf(ticket, booking).subject;
  return detail ? `${kind} · ${detail}` : kind;
};

// The icon tile: a report (a flag) and a system ticket (an hourglass) in amber, a request (a
// question and answer) in blue.
export const ticketTone = (ticket) => {
  if (ticket?.kind === "reported_lesson") return { tone: "warning", icon: "flag" };
  if (ticket?.kind === "unresolved_lesson") return { tone: "warning", icon: "hourglass" };
  return { tone: "brand", icon: "question" };
};

// Which side raised the ticket: "tutor" or "student", from the lesson's parties or, on a request,
// from the raiser's role. null for a system ticket, for a request whose raiser is not read yet, and
// for a raiser who is neither a student nor a tutor (a staff or former staff account).
export const raiserSide = (ticket, users = {}) => {
  const id = ticket?.raisedBy;
  if (!id) return null;
  if (id === ticket.teacherId) return "tutor";
  if (id === ticket.studentId) return "student";
  const role = users[id]?.role;
  if (!role) return null;
  return role === "teacher" ? "tutor" : role === "student" ? "student" : null;
};

// The head's line of facts: "#12 · Opened 30 Aug 2026, 19:12 by Barakat Sami, parent · for Maryam ·
// Tutor Mahmoud Essam". A name not read yet is left out, never shown as an id.
export const ticketMeta = (ticket, users = {}, { timeZone } = {}) => {
  const nameOf = (id) => (id ? personName(users[id]) : "");
  const raiser = nameOf(ticket?.raisedBy);
  const staffTicket = ticket?.source === "staff";
  const when = at(ticket?.raisedAt, timeZone);
  let opened = when ? `Opened ${when}` : "Opened";
  if (!ticket?.raisedBy) opened += " by the system";
  else if (staffTicket) opened += nameOf(ticket.openedBy) ? ` by ${nameOf(ticket.openedBy)}, staff` : " by staff";
  else if (raiser) {
    const role = roleWord(users[ticket.raisedBy]);
    opened += ` by ${raiser}${role ? `, ${role.toLowerCase()}` : ""}`;
  }
  const side = raiserSide(ticket, users);
  const student = nameOf(ticket?.studentId);
  const tutor = nameOf(ticket?.teacherId);
  const facts = [ticketNumberLabel(ticket).label, opened];
  if (staffTicket && raiser) facts.push(`for ${raiser}`);
  // Only a report stores the learner's first name (reportLesson.js). An adult's own lesson is for
  // no one else, and until the student's document is read the page cannot tell which it is.
  if (ticket?.learnerName && student && ticket.learnerName !== student.split(/\s+/)[0]) {
    facts.push(`for ${ticket.learnerName}`);
  }
  if (student && side !== "student") facts.push(`Student ${student}`);
  if (tutor && side !== "tutor") facts.push(`Tutor ${tutor}`);
  return facts.join(" · ");
};

// ── What was reported (D60) ──

// The opening message: reportLesson.js and createTicket.js save the ticket's text as its first
// message, sent by the raiser, or on a staff ticket by the staff member who opened it. The report
// card shows it, so the thread starts after it. A report's opening message is its reason word for
// word; a first message from anyone else, or one with an image, stays in the thread.
export const splitOpening = (ticket, messages = []) => {
  const first = messages[0];
  const opener = ticket?.source === "staff" ? ticket?.openedBy : ticket?.raisedBy;
  const matches = ticket?.kind === "reported_lesson" ? first?.text === ticket?.evidence?.reason : true;
  return first && opener && first.senderId === opener && !first.attachmentPath && matches
    ? { opening: first, thread: messages.slice(1) }
    : { opening: null, thread: messages };
};

const SYSTEM_TEXT =
  "Nobody recorded an outcome for this lesson within seven days of its end, so the system raised this ticket. No money has moved.";

const yesNo = (value) => (value === true ? "yes" : value === false ? "no" : "not recorded");
const timeOnly = (value, timeZone) => dateTimeLines(value, { timeZone })?.time || "not recorded";

// The report card: its caption, title and chip, the text as sent ("" when there is none), and the
// foot line of what the ticket copied from the lesson ("" when there is nothing to say).
export const reportCard = (ticket, { opening = null, users = {}, booking, timeZone } = {}) => {
  const when = at(ticket?.raisedAt, timeZone);
  const suffix = when ? ` · ${when}` : "";
  const side = raiserSide(ticket, users);
  const lesson = ticketLessonLabel(lessonOf(ticket, booking), { timeZone });
  const evidence = ticket?.evidence || {};
  if (ticket?.kind === "reported_lesson") {
    return {
      caption: side ? `What the ${side} reported` : "What was reported",
      title: lesson,
      chip: `Reported in the app${suffix}`,
      text: evidence.reason || opening?.text || "",
      foot: `Copied from the lesson: ends ${timeOnly(evidence.endTimeUTC, timeZone)} · tutor paid: ${yesNo(evidence.countForTeacher)} · free session: ${yesNo(evidence.isFreeSession)}`,
    };
  }
  if (ticket?.kind === "unresolved_lesson") {
    return {
      caption: "Raised by the system",
      title: lesson,
      chip: `Raised by the system${suffix}`,
      text: SYSTEM_TEXT,
      foot: `Copied from the lesson: student joined ${timeOnly(evidence.studentJoinedAt, timeZone)} · tutor joined ${timeOnly(evidence.teacherJoinedAt, timeZone)} · ends ${timeOnly(evidence.endTimeUTC, timeZone)}`,
    };
  }
  const staff = ticket?.source === "staff";
  return {
    caption: staff ? "Opened by staff" : side ? `What the ${side} asked` : "What was asked",
    title: categoryLabel(ticket?.category) || kindLabel(ticket?.kind) || "Ticket",
    chip: `${staff ? "Opened by staff" : "Sent from the app"}${suffix}`,
    text: opening?.text || "",
    foot: ticket?.bookingId ? `About the lesson: ${lesson}` : "",
  };
};

// ── Session evidence (D62) ──

const PARTY_WORDS = { learner: "by the student", tutor: "by the tutor", staff: "by staff" };

// Whole minutes from the lesson's start to a join, rounded down; null without both times.
const minutesLate = (start, value) => {
  const from = toDate(start);
  const to = toDate(value);
  return from && to ? Math.floor((to.getTime() - from.getTime()) / 60000) : null;
};

// The card's rows, from the lesson document, or from the copies on the ticket when the lesson could
// not be read (`booking` null). `alert` marks a value the page draws in red: a late join, or no join
// recorded once the lesson has ended, unless it was cancelled.
export const evidenceRows = (ticket, booking, { now = new Date(), timeZone } = {}) => {
  const lesson = booking || {
    startTimeUTC: ticket?.startTimeUTC,
    endTimeUTC: ticket?.evidence?.endTimeUTC,
    studentJoinedAt: ticket?.evidence?.studentJoinedAt,
    teacherJoinedAt: ticket?.evidence?.teacherJoinedAt,
  };
  const end = endOf(lesson);
  const ended = Boolean(end) && end.getTime() <= now.getTime();
  const scheduled = dateTimeLines(lesson.startTimeUTC, { timeZone, weekday: true });
  const joined = (key, label, value) => {
    const time = dateTimeLines(value, { timeZone })?.time;
    if (!time) return { key, label, value: "Not recorded", alert: ended && booking?.status !== "Cancelled" };
    const late = minutesLate(lesson.startTimeUTC, value);
    return late >= 1
      ? { key, label, value: `${time} · ${late} min late`, alert: true }
      : { key, label, value: time, alert: false };
  };
  const rows = [
    {
      key: "scheduled",
      label: "Scheduled",
      value: scheduled ? `${scheduled.date} · ${scheduled.time}` : "Not recorded",
      alert: false,
    },
    joined("student", "Student joined", lesson.studentJoinedAt),
    joined("tutor", "Tutor joined", lesson.teacherJoinedAt),
    {
      key: "end",
      label: ended || !end ? "Ended" : "Ends",
      value: end ? dateTimeLines(end, { timeZone }).time : "Not recorded",
      alert: false,
    },
  ];
  const fields =
    booking?.status === "Finished"
      ? ["finishedAt", "finishedBy"]
      : booking?.status === "Cancelled"
        ? ["cancelledAt", "cancelledBy"]
        : null;
  if (fields) {
    const by = booking[fields[1]];
    rows.push({
      key: "outcome",
      label: statusWord(booking.status),
      value: [at(booking[fields[0]], timeZone), by ? PARTY_WORDS[partyOf(booking, by)] : ""].filter(Boolean).join(" · ") || "Not recorded",
      alert: false,
    });
  }
  return rows;
};

// ── The person cards (D62) ──

const joinedLine = (role, user, timeZone) => {
  const joined = dateTimeLines(user?.createdAt, { timeZone })?.date;
  return joined ? `${role} · Joined ${joined}` : role;
};

// The student and tutor cards: the lesson's student and tutor, or a request's raiser in the card
// for their side. Each card: the id, the name ("—" until read), the head's line, one row (Wallet or
// Rating), and the link with the permission it needs.
export const personCards = (ticket, users = {}, { timeZone } = {}) => {
  const side = raiserSide(ticket, users);
  const studentId = ticket?.studentId || (side === "student" ? ticket.raisedBy : null);
  const tutorId = ticket?.teacherId || (side === "tutor" ? ticket.raisedBy : null);
  const cards = [];
  if (studentId) {
    const user = users[studentId];
    cards.push({
      key: "student",
      id: studentId,
      name: personName(user) || "—",
      line: joinedLine(user ? typeChip(user) : "Student", user, timeZone),
      row: { key: "wallet", label: "Wallet", value: user ? sessionsLabel(user.WalletBalance) : "—" },
      to: `/students/${studentId}`,
      linkLabel: "Open student page",
      permission: permissions.VIEW_STUDENTS,
    });
  }
  if (tutorId) {
    const user = users[tutorId];
    cards.push({
      key: "tutor",
      id: tutorId,
      name: personName(user) || "—",
      line: joinedLine("Tutor", user, timeZone),
      row: { key: "rating", label: "Rating", value: user ? reviewsLine(user) : "—" },
      to: `/tutors/${tutorId}`,
      linkLabel: "Open tutor page",
      permission: permissions.VIEW_TUTORS,
    });
  }
  return cards;
};

// ── The timeline (D62) ──

const OPENED = { reported_lesson: "Report sent from the app", unresolved_lesson: "Raised by the system" };

// Oldest first: opened, the first staff reply, the last message, resolved. A status move stores no
// time, so it is not here. Each event: key, tone (the ring), icon, title, sub, at (a Date), time.
export const timelineEvents = (ticket, thread = [], users = {}, { timeZone } = {}) => {
  const nameOf = (id) => (id ? personName(users[id]) : "");
  const staffTicket = ticket?.source === "staff";
  const events = [];
  const add = (key, tone, icon, title, sub, value) => {
    const date = toDate(value);
    if (date) events.push({ key, tone, icon, title, sub, at: date, time: at(date, timeZone) });
  };
  add(
    "opened",
    "brand",
    "opened",
    OPENED[ticket?.kind] || (staffTicket ? "Opened by staff" : "Request sent from the app"),
    ticket?.raisedBy ? nameOf(staffTicket ? ticket.openedBy : ticket.raisedBy) : "No outcome seven days after the lesson",
    ticket?.raisedAt
  );
  const first = thread.find((m) => isStaffMessage(ticket, m));
  if (first) {
    add("reply", "success", "reply", ticket?.raisedBy ? "First reply sent" : "First note", nameOf(first.senderId), first.createdAt);
  }
  const last = thread[thread.length - 1];
  if (last && last !== first) add("last", "neutral", "message", "Last message", nameOf(last.senderId), last.createdAt);
  if (ticket?.resolvedAt) {
    const credit = ticket.resolution?.creditSessions;
    const reopened = ticket.status === "open" || ticket.status === "waiting_user";
    const sub = [nameOf(ticket.resolvedBy), credit ? `credited ${sessionsLabel(credit)}` : "", reopened ? "reopened since" : ""];
    add("resolved", "success", "resolved", "Resolved", sub.filter(Boolean).join(" · "), ticket.resolvedAt);
  }
  return events.sort((a, b) => a.at.getTime() - b.at.getTime());
};

// ── Actions (D59, D61) ──

const MOVES = { open: ["waiting_user"], waiting_user: ["open"], resolved: ["closed"] };

// Move to ▾: the moves setTicketStatus allows from the ticket's status. Waiting for You waits on a
// person, so a system ticket never gets it.
export const moveOptions = (ticket) =>
  (MOVES[ticket?.status] || [])
    .filter((status) => status !== "waiting_user" || Boolean(ticket?.raisedBy))
    .map((status) => ({ status, label: STATUS_LABELS[status] }));

// Resolve is offered while the ticket is Submitted or Waiting for You; resolveTicket refuses the rest.
export const canResolve = (ticket) => ticket?.status === "open" || ticket?.status === "waiting_user";

// A credit moves in half sessions and is never below one half, as in the old Resolve dialog.
export const CREDIT_STEP = 0.5;
export const creditValue = (raw) => Math.max(CREDIT_STEP, Math.round((Number(raw) || CREDIT_STEP) * 2) / 2);

// The Resolution card's words (D61): its intro, the reason box's hint, the credit's label and the
// "What happens" line. `person`: a person raised the ticket; `name`: theirs ("" until read).
export const resolutionText = ({ person = false, name = "", credit = null } = {}) => {
  const who = name || "the person";
  const happens = [
    "What happens: the ticket moves to Resolved",
    person && credit ? `${sessionsLabel(credit)} ${credit === 1 ? "goes" : "go"} to ${who}'s wallet` : "",
    person ? `${who} gets a notice in the app` : "",
  ].filter(Boolean);
  return {
    intro: person
      ? `The reason is saved on the ticket and in the activity log. ${name || "The person"} sees it in the app.`
      : "The reason is saved on the ticket and in the activity log.",
    hint: person ? `Reason for ${who} and the activity log · required` : "Reason for the activity log · required",
    creditLabel: `Credit sessions to ${name ? `${name}'s` : "their"} wallet`,
    happens: `${happens.join(" · ")}.`,
  };
};

// A resolved or closed ticket's stored resolution, as label and value rows.
export const resolutionRows = (ticket, users = {}, { timeZone } = {}) => {
  const credit = ticket?.resolution?.creditSessions;
  return [
    { key: "by", label: "Resolved by", value: personName(users[ticket?.resolvedBy]) || "—" },
    { key: "when", label: "When", value: at(ticket?.resolvedAt, timeZone) || "—" },
    { key: "credit", label: "Credit", value: credit ? sessionsLabel(credit) : "None" },
  ];
};

// ── The conversation (D60) ──

// The card's title and line, the composer's hint, and the empty thread's line.
export const conversationText = (ticket, users = {}) => {
  if (!ticket?.raisedBy) {
    return {
      title: "Staff notes",
      line: "The system raised this ticket, so only staff can read these messages.",
      placeholder: "Write a note for staff.",
      empty: "No notes yet.",
    };
  }
  const side = raiserSide(ticket, users);
  const name = personName(users[ticket.raisedBy]);
  return {
    title: side ? `Conversation with the ${side}` : "Conversation",
    line: `${name || "The person who raised this ticket"} sees these messages in the app's support chat.`,
    placeholder: `Write to ${name || "them"}. Say what you found and what happens next.`,
    empty: "No replies yet.",
  };
};

// The chip above the thread: "Ticket #12 opened · 30 Aug 2026, 19:12".
export const openedChip = (ticket, { timeZone } = {}) => {
  const when = at(ticket?.raisedAt, timeZone);
  return `Ticket ${ticketNumberLabel(ticket).label} opened${when ? ` · ${when}` : ""}`;
};

// The line under a message: "Hana Mostafa · Support · 30 Aug 2026, 19:20" on staff's side, the
// person's name and role on theirs. `staff` says which side the message sits on.
export const messageMeta = (ticket, message, users = {}, { timeZone } = {}) => {
  const staff = isStaffMessage(ticket, message);
  const user = users[message?.senderId];
  const who = personName(user) || (staff ? "Staff" : "—");
  return {
    staff,
    line: [who, staff ? "Support" : roleWord(user), at(message?.createdAt, timeZone)].filter(Boolean).join(" · "),
  };
};

// "3 messages", "1 event".
export const messageCount = (n) => `${countText(n)} ${n === 1 ? "message" : "messages"}`;
export const eventCount = (n) => `${countText(n)} ${n === 1 ? "event" : "events"}`;
